/**
 * Step 2: Domicilio del Cliente
 * Versión refactorizada con TanStack Form
 */
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ProvinciaCombobox } from '@/components/ui/provincia-combobox'
import { MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getFieldError, type ClienteForm } from '../useClienteForm'

interface StepDomicilioProps {
  form: ClienteForm
}

export function StepDomicilioNew({ form }: StepDomicilioProps) {
  return (
    <div className="space-y-6">
      {/* Header con icono */}
      <div className="flex items-center gap-3 pb-2 border-b">
        <div className="p-2 rounded-lg bg-primary/10">
          <MapPin className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-medium">Domicilio Fiscal</h3>
          <p className="text-sm text-muted-foreground">Dirección del cliente para fines impositivos</p>
        </div>
      </div>

      {/* Provincia - Destacada y Requerida */}
      <form.Field name="domicilio_provincia">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          return (
            <div className="space-y-2">
              <Label htmlFor="domicilio_provincia" className="text-base font-semibold">
                Provincia *
              </Label>
              <ProvinciaCombobox
                value={field.state.value || null}
                onValueChange={(value) => {
                  field.handleChange(value ?? '')
                  // También actualizar provincia fiscal
                  form.setFieldValue('provincia', value ?? '')
                }}
                placeholder="Seleccionar provincia"
                className={cn(hasError && 'border-destructive')}
              />
              <p className={cn('text-sm h-5', hasError ? 'text-destructive' : 'text-muted-foreground')}>
                {hasError
                  ? getFieldError(field.state.meta.errors)
                  : 'La provincia se usará para determinar el régimen de Ingresos Brutos.'}
              </p>
            </div>
          )
        }}
      </form.Field>

      {/* Localidad y CP */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.Field name="domicilio_localidad">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="domicilio_localidad">Localidad</Label>
              <Input
                id="domicilio_localidad"
                placeholder="Ciudad"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>

        <form.Field name="domicilio_cp">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="domicilio_cp">Código Postal</Label>
              <Input
                id="domicilio_cp"
                placeholder="4000"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>
      </div>

      {/* Calle y Número */}
      <div className="grid grid-cols-3 gap-4">
        <form.Field name="domicilio_calle">
          {(field) => (
            <div className="col-span-2 space-y-2">
              <Label htmlFor="domicilio_calle">Calle</Label>
              <Input
                id="domicilio_calle"
                placeholder="Nombre de la calle"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>

        <form.Field name="domicilio_numero">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="domicilio_numero">Número</Label>
              <Input
                id="domicilio_numero"
                placeholder="123"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>
      </div>

      {/* Piso y Departamento */}
      <div className="grid grid-cols-2 gap-4">
        <form.Field name="domicilio_piso">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="domicilio_piso">Piso</Label>
              <Input
                id="domicilio_piso"
                placeholder="1"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>

        <form.Field name="domicilio_depto">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="domicilio_depto">Departamento</Label>
              <Input
                id="domicilio_depto"
                placeholder="A"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>
      </div>

      {/* Barrio y Referencia */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.Field name="domicilio_barrio">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="domicilio_barrio">Barrio</Label>
              <Input
                id="domicilio_barrio"
                placeholder="Ej: Yerba Buena"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>

        <form.Field name="domicilio_referencia">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="domicilio_referencia">Referencia</Label>
              <Input
                id="domicilio_referencia"
                placeholder="Ej: Frente a la plaza"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                className="h-11"
              />
            </div>
          )}
        </form.Field>
      </div>
    </div>
  )
}
